import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CommentThread from './CommentThread';

const APPETITE_LABEL = {
  small: 'Small batch',
  big: 'Big batch',
};

const STATUS_LABEL = {
  shaping: 'Shaping',
  ready: 'Ready to bet on',
  bet: 'Bet placed',
  archived: 'Archived',
};

const STATUS_COLOR = {
  shaping: 'default',
  ready: 'primary',
  bet: 'success',
  archived: 'default',
};

const Section = ({ label, body, color }) => {
  if (!body) return null;
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: '.08em', color: color || 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{body}</Typography>
    </Box>
  );
};

/**
 * One pitch on the betting table.
 *
 * Collapsed it shows what a betting conversation needs: the problem and the
 * appetite. Expanded it shows the shape and the guardrails.
 */
const PitchCard = ({ pitch, onEdit, onDelete, onBet, onMarkReady, canBet = true }) => {
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const closeMenu = () => setMenuAnchor(null);

  const hasDetail = Boolean(pitch.solution || pitch.rabbitHoles || pitch.noGos);

  return (
    <Card sx={{ mb: 2 }} variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{pitch.title}</Typography>
              <Chip size="small" variant="outlined" label={APPETITE_LABEL[pitch.appetite]} />
              <Chip
                size="small"
                variant={pitch.status === 'ready' ? 'filled' : 'outlined'}
                color={STATUS_COLOR[pitch.status]}
                label={STATUS_LABEL[pitch.status]}
              />
              {pitch.status === 'ready' && !pitch.isStale && pitch.daysReady !== null && (
                <Typography variant="caption" color="text.disabled">
                  {pitch.daysReady === 0 ? 'ready today' : `ready ${pitch.daysReady} days`}
                </Typography>
              )}
            </Box>

            {pitch.problem && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                {pitch.problem}
              </Typography>
            )}
          </Box>

          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Pitch actions">
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
            <MenuItem onClick={() => { closeMenu(); onEdit(pitch); }}>
              <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Reshape
            </MenuItem>
            {pitch.status !== 'ready' && pitch.status !== 'bet' && (
              <MenuItem onClick={() => { closeMenu(); onMarkReady(pitch); }}>
                <CasinoOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Put it on the table
              </MenuItem>
            )}
            <MenuItem onClick={() => { closeMenu(); onDelete(pitch); }}>
              <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>
        </Box>

        {/* Going stale is the mechanism that keeps this from becoming a backlog,
            so it is stated as something to act on rather than a quiet flag. */}
        {pitch.isStale && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.25 }}>
            <Typography variant="caption">
              On the table for {pitch.daysReady} days without a bet. Shape it again or let it
              go: an idea that has waited this long is answering a question the team has
              stopped asking.
            </Typography>
          </Alert>
        )}

        {hasDetail && (
          <>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, cursor: 'pointer' }}
              onClick={() => setOpen(current => !current)}
            >
              <Typography variant="caption" color="text.secondary">
                {open ? 'Hide the shape' : 'See the shape, rabbit holes, and no-gos'}
              </Typography>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Box>

            <Collapse in={open}>
              <Stack spacing={1.5} sx={{ mt: 1.5 }} divider={<Divider flexItem />}>
                <Section label="SOLUTION" body={pitch.solution} />
                <Section label="RABBIT HOLES" body={pitch.rabbitHoles} color="warning.main" />
                <Section label="NO-GOS" body={pitch.noGos} color="error.main" />
              </Stack>
            </Collapse>
          </>
        )}

        {pitch.status === 'ready' && canBet && (
          <Box sx={{ mt: 2 }}>
            <Tooltip title="Create a bet from this pitch in a cycle">
              <Button
                size="small"
                variant="contained"
                startIcon={<CasinoOutlinedIcon />}
                onClick={() => onBet(pitch)}
              >
                Bet on this
              </Button>
            </Tooltip>
          </Box>
        )}

        <Divider sx={{ mt: 2, mb: 0.5 }} />
        <CommentThread commentableType="pitch" commentableId={pitch.id} />
      </CardContent>
    </Card>
  );
};

export default PitchCard;
